import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Absence : famille "indisponibilité déclarée".
 *
 * Indisponibilité d'une personne sur une période, avec un sous-type et un
 * circuit de validation. Construite sur le patron "famille + type" (cf. asset,
 * le PATRON) : le socle ne connaît pas d'absence concrète ("congé payé",
 * "arrêt", "formation"), il connaît la famille générique `absence` ; le
 * vocabulaire métier vit dans le champ `type` (chaîne libre déclarée par l'app),
 * jamais dans le nom de collection. L'état du circuit de validation vit dans
 * `status` (ex. "demande", "valide", "refuse").
 *
 * Les liens vers d'autres familles (worker, shift, availability, booking,
 * credit) sont de simples ids : aucune dépendance de schéma entre familles.
 * `workerId` (la personne indisponible) est le CŒUR de la famille, donc requis ;
 * les autres liens et le valideur `approvedByWorkerId` restent optionnels (une
 * absence peut être créée avant d'être validée ou rattachée à un shift précis).
 *
 * Sécurité / RGPD (cf. fiche) : le planning org-wide ne voit que la période et
 * un statut indisponible, SANS motif. Le motif (le `kind`, ex. maladie) et le
 * justificatif sont des données de santé : ils NE vivent PAS dans ce document
 * org-wide en v0 ; ils iront dans un sous-document privé restreint à
 * l'intéressé, au valideur et au rôle RH (cf. WIP). Ne pas les ajouter ici.
 *
 * Convention de patron (cf. asset) :
 *   - `absenceCoreFields`        : les champs MÉTIER seuls (sans champs standard).
 *   - `AbsenceSchema`            : famille complète = champs standard + métier.
 *   - `AbsenceCreateInputSchema` : ce que l'appelant fournit à la création
 *     (champs métier ; les champs standard sont remplis par le SDK). Sert à
 *     VALIDER l'entrée AVANT toute écriture Firestore.
 */

/** Champs métier de la famille absence (hors champs standard du socle). */
export const absenceCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "conge", "arret", "formation"). */
  type: z.string().min(1),
  /** Personne indisponible (réf. famille worker, cœur de la famille). */
  workerId: z.string().min(1),
  /** Début de la période d'indisponibilité, optionnel. */
  startAt: z.date().optional(),
  /** Fin de la période d'indisponibilité, optionnel. */
  endAt: z.date().optional(),
  /** État du circuit de validation, chaîne libre pilotée par l'app (ex. "demande", "valide", "refuse"). */
  status: z.string().min(1).optional(),
  /** Valideur ayant approuvé l'absence (réf. famille worker), optionnel. */
  approvedByWorkerId: z.string().min(1).optional(),
  /** Shift impacté par l'indisponibilité (réf. famille shift), optionnel. */
  shiftId: z.string().min(1).optional(),
  /** Règle de disponibilité visée (réf. famille availability), optionnel. */
  availabilityId: z.string().min(1).optional(),
  /** Réservation liée si l'absence en découle (réf. famille booking), optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Compteur / crédit de congés impacté (réf. famille credit), optionnel. */
  creditId: z.string().min(1).optional(),
  // kind (motif, ex. maladie) et justificatif : sensibles (données de santé),
  // iront dans un sous-document privé restreint à l'intéressé, au valideur et au
  // rôle RH (cf. WIP), hors v0. Ne pas les inclure dans ce document org-wide.
} as const;

/** Schéma complet de la famille absence (champs standard + champs métier). */
export const AbsenceSchema = z.object({
  ...baseEntityFields,
  ...absenceCoreFields,
});

/** Type d'une absence. */
export type Absence = z.infer<typeof AbsenceSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const AbsenceCreateInputSchema = z.object({
  ...absenceCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une absence. */
export type AbsenceCreateInput = z.infer<typeof AbsenceCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const AbsenceUpdateInputSchema = z
  .object({ ...absenceCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'une absence. */
export type AbsenceUpdateInput = z.infer<typeof AbsenceUpdateInputSchema>;
